/**
 * Reading Torn's API answers (v1 shapes, as `/user/?selections=…` returns them) into what the
 * website shows. Pure: no page, no network — unit-tested in apps/web/test.
 */

type Obj = Record<string, unknown>;
export const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
export const n = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};
const s = (x: unknown): string => (typeof x === 'string' ? x : x === null || x === undefined ? '' : String(x));
/** Torn's unix seconds → ms (0 / missing → null). */
const ms = (x: unknown): number | null => {
  const v = n(x);
  return v && v > 0 ? (v < 1e12 ? v * 1000 : v) : null;
};

export interface Status {
  name: string;
  playerId: number | null;
  level: number | null;
  state: string;
  description: string;
  details: string;
  color: string;
  until: number | null;
  lastAction: string;
  lastActionAt: number | null;
  faction: { id: number; name: string; position: string } | null;
  life: { current: number; maximum: number } | null;
}

export function readStatus(body: unknown): Status | null {
  const b = obj(body);
  if (!b) return null;
  const st = obj(b.status);
  if (!st && !b.name) return null;
  const la = obj(b.last_action);
  const f = obj(b.faction);
  const life = obj(b.life);
  const fid = n(f?.faction_id);
  return {
    name: s(b.name),
    playerId: n(b.player_id),
    level: n(b.level),
    state: s(st?.state) || 'Okay',
    description: s(st?.description),
    details: s(st?.details).replace(/<[^>]*>/g, ''),
    color: s(st?.color) || 'green',
    until: ms(st?.until),
    lastAction: s(la?.relative),
    lastActionAt: ms(la?.timestamp),
    faction: fid ? { id: fid, name: s(f?.faction_name), position: s(f?.position) } : null,
    life: life && n(life.current) !== null ? { current: n(life.current)!, maximum: n(life.maximum) ?? 0 } : null,
  };
}

export interface Bar {
  current: number;
  maximum: number;
  /** When it's full (ms), null if full already. */
  fullAt: number | null;
}

export function readBars(body: unknown, now: number): Partial<Record<'energy' | 'nerve' | 'happy' | 'life', Bar>> & { chain?: { current: number; maximum: number; timeout: number; cooldown: number } } {
  const b = obj(body);
  const out: ReturnType<typeof readBars> = {};
  if (!b) return out;
  for (const k of ['energy', 'nerve', 'happy', 'life'] as const) {
    const o = obj(b[k]);
    const cur = n(o?.current);
    const max = n(o?.maximum);
    if (cur === null || max === null) continue;
    const full = n(o?.fulltime) ?? 0;
    out[k] = { current: cur, maximum: max, fullAt: full > 0 ? now + full * 1000 : null };
  }
  const c = obj(b.chain);
  if (c && n(c.current) !== null) out.chain = { current: n(c.current)!, maximum: n(c.maximum) ?? 0, timeout: n(c.timeout) ?? 0, cooldown: n(c.cooldown) ?? 0 };
  return out;
}

export interface Cooldowns {
  drug: number;
  booster: number;
  medical: number;
}

export function readCooldowns(body: unknown): Cooldowns | null {
  const c = obj(obj(body)?.cooldowns);
  if (!c) return null;
  return { drug: n(c.drug) ?? 0, booster: n(c.booster) ?? 0, medical: n(c.medical) ?? 0 };
}

export interface Travel {
  destination: string;
  method: string;
  departed: number | null;
  arrival: number | null;
  /** Seconds left in the air (0: landed). */
  timeLeft: number;
}

export function readTravel(body: unknown): Travel | null {
  const t = obj(obj(body)?.travel);
  if (!t) return null;
  return { destination: s(t.destination) || 'Torn', method: s(t.method), departed: ms(t.departed), arrival: ms(t.timestamp), timeLeft: n(t.time_left) ?? 0 };
}

export interface Refills {
  energy: boolean;
  nerve: boolean;
  token: boolean;
  special: number;
}

export function readRefills(body: unknown): Refills | null {
  const r = obj(obj(body)?.refills);
  if (!r) return null;
  return { energy: r.energy_refill_used === true, nerve: r.nerve_refill_used === true, token: r.token_refill_used === true, special: n(r.special_refills_available) ?? 0 };
}

export interface Money {
  wallet: number | null;
  points: number | null;
  bank: { amount: number; until: number | null } | null;
  vault: number | null;
  cayman: number | null;
  company: number | null;
  dailyNetworth: number | null;
}

export function readMoney(body: unknown): Money | null {
  const b = obj(body);
  if (!b || (b.money_onhand === undefined && b.points === undefined)) return null;
  const bank = obj(b.city_bank);
  return {
    wallet: n(b.money_onhand),
    points: n(b.points),
    bank: bank && n(bank.amount) ? { amount: n(bank.amount)!, until: n(bank.time_left) ? Date.now() + n(bank.time_left)! * 1000 : null } : null,
    vault: n(b.vault_amount),
    cayman: n(b.cayman_bank),
    company: n(b.company_funds),
    dailyNetworth: n(b.daily_networth),
  };
}

const NW_LABELS: Record<string, string> = {
  wallet: 'Wallet',
  bank: 'City bank',
  points: 'Points',
  cayman: 'Cayman',
  vault: 'Vault',
  piggybank: 'Piggy bank',
  items: 'Items',
  displaycase: 'Display case',
  bazaar: 'Bazaar',
  itemmarket: 'Item market',
  trade: 'Trades',
  properties: 'Properties',
  stockmarket: 'Stocks',
  auctionhouse: 'Auction house',
  company: 'Company',
  bookie: 'Bookie',
  enlistedcars: 'Cars',
  loan: 'Loan',
  unpaidfees: 'Unpaid fees',
  pending: 'Pending',
};

export function readNetworth(body: unknown): { total: number; parts: Array<{ label: string; value: number }> } | null {
  const nw = obj(obj(body)?.networth);
  const total = n(nw?.total);
  if (!nw || total === null) return null;
  const parts = Object.entries(nw)
    .filter(([k]) => k !== 'total' && k !== 'parsetime')
    .map(([k, v]) => ({ label: NW_LABELS[k] ?? k, value: n(v) ?? 0 }))
    .filter((p) => p.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  return { total, parts };
}

export interface BattleStats {
  strength: number;
  defense: number;
  speed: number;
  dexterity: number;
  total: number;
  /** Percent modifiers (+12 = 12% more in fights). */
  mods: { strength: number; defense: number; speed: number; dexterity: number };
  info: string[];
}

export function readBattleStats(body: unknown): BattleStats | null {
  const b = obj(body);
  if (!b) return null;
  const v = (k: string) => n(obj(b[k])?.value ?? b[k]);
  const [st, de, sp, dx] = [v('strength'), v('defense'), v('speed'), v('dexterity')];
  if (st === null || de === null || sp === null || dx === null) return null;
  const mod = (k: string) => n(b[`${k}_modifier`] ?? obj(b[k])?.modifier) ?? 0;
  const info = ['strength_info', 'defense_info', 'speed_info', 'dexterity_info'].flatMap((k) => (Array.isArray(b[k]) ? (b[k] as unknown[]).map((x) => s(x)).filter(Boolean) : []));
  return { strength: st, defense: de, speed: sp, dexterity: dx, total: st + de + sp + dx, mods: { strength: mod('strength'), defense: mod('defense'), speed: mod('speed'), dexterity: mod('dexterity') }, info };
}

export interface Attack {
  id: string;
  started: number;
  ended: number | null;
  attacker: { id: number | null; name: string; faction: string };
  defender: { id: number | null; name: string; faction: string };
  result: string;
  respect: number;
  chain: number;
  /** Did I attack (true) or was I attacked? */
  outgoing: boolean;
  /** Did I come out on top? */
  won: boolean;
  stealth: boolean;
  fairFight: number | null;
}

const WIN_OUT = /^(attacked|mugged|hospitalized|arrested|special|looted|bounty)$/i;
/** Attacked by someone: I held them off when it ended like this. */
const HELD = /^(lost|stalemate|escape|timeout|interrupted)$/i;

export function readAttacks(body: unknown, me: number): Attack[] {
  const list = obj(obj(body)?.attacks);
  if (!list) return [];
  return Object.entries(list)
    .map(([id, raw]) => {
      const a = obj(raw) ?? {};
      const attackerId = n(a.attacker_id);
      const outgoing = attackerId === me;
      const result = s(a.result);
      const won = outgoing ? WIN_OUT.test(result) : HELD.test(result);
      const mods = obj(a.modifiers);
      return {
        id: s(a.code) || id,
        started: ms(a.timestamp_started) ?? 0,
        ended: ms(a.timestamp_ended),
        attacker: { id: attackerId, name: s(a.attacker_name) || (a.stealthed ? 'Someone' : ''), faction: s(a.attacker_factionname) },
        defender: { id: n(a.defender_id), name: s(a.defender_name), faction: s(a.defender_factionname) },
        result,
        // Torn's respect is the attacker's: only yours when you attacked.
        respect: outgoing ? (n(a.respect_gain ?? a.respect) ?? 0) : 0,
        chain: n(a.chain) ?? 0,
        outgoing,
        won,
        stealth: a.stealthed === 1 || a.stealthed === true,
        fairFight: n(mods?.fair_fight),
      };
    })
    .sort((x, y) => y.started - x.started);
}

export interface LogEntry {
  id: string;
  type: number;
  at: number;
  title: string;
  category: string;
  data: Record<string, unknown>;
}

export function readLogs(body: unknown): LogEntry[] {
  const list = obj(obj(body)?.log);
  if (!list) return [];
  return Object.entries(list)
    .map(([id, raw]) => {
      const l = obj(raw) ?? {};
      return { id, type: n(l.log) ?? 0, at: ms(l.timestamp) ?? 0, title: s(l.title), category: s(l.category), data: obj(l.data) ?? {} };
    })
    .sort((a, b) => b.at - a.at);
}

/** Sums over a set of logs, torn.report style: per category and per kind, and the usual totals. */
export function summariseLogs(logs: readonly LogEntry[]) {
  const byCategory = new Map<string, number>();
  const byTitle = new Map<string, { count: number; category: string }>();
  let energyGym = 0;
  const gains: Record<string, number> = {};
  let xanax = 0;
  for (const l of logs) {
    byCategory.set(l.category || 'Other', (byCategory.get(l.category || 'Other') ?? 0) + 1);
    const t = byTitle.get(l.title) ?? { count: 0, category: l.category };
    t.count++;
    byTitle.set(l.title, t);
    if (/^gym train/i.test(l.title)) {
      energyGym += n(l.data.energy_used) ?? 0;
      for (const stat of ['strength', 'defense', 'speed', 'dexterity']) {
        const g = n(l.data[`${stat}_increased`] ?? l.data[`${stat}_gained`]);
        if (g) gains[stat] = (gains[stat] ?? 0) + g;
      }
    }
    if (/xanax/i.test(l.title) || (/item use/i.test(l.title) && n(l.data.item) === 206)) xanax++;
  }
  return {
    byCategory: [...byCategory].map(([category, count]) => ({ category, count })).sort((a, b) => b.count - a.count),
    byTitle: [...byTitle].map(([title, v]) => ({ title, ...v })).sort((a, b) => b.count - a.count),
    energyGym,
    gains,
    xanax,
    from: logs.length ? logs[logs.length - 1]!.at : null,
    to: logs.length ? logs[0]!.at : null,
  };
}

export interface FactionMember {
  id: number;
  name: string;
  level: number;
  position: string;
  days: number;
  state: string;
  description: string;
  color: string;
  until: number | null;
  lastAction: string;
  lastActionAt: number | null;
  online: string;
}

export function readFaction(body: unknown): { id: number | null; name: string; tag: string; respect: number | null; members: FactionMember[] } | null {
  const b = obj(body);
  const members = obj(b?.members);
  if (!b || !members) return null;
  return {
    id: n(b.ID ?? b.id),
    name: s(b.name),
    tag: s(b.tag),
    respect: n(b.respect),
    members: Object.entries(members).map(([id, raw]) => {
      const m = obj(raw) ?? {};
      const st = obj(m.status);
      const la = obj(m.last_action);
      return {
        id: Number(id),
        name: s(m.name),
        level: n(m.level) ?? 0,
        position: s(m.position),
        days: n(m.days_in_faction) ?? 0,
        state: s(st?.state) || 'Okay',
        description: s(st?.description),
        color: s(st?.color) || 'green',
        until: ms(st?.until),
        lastAction: s(la?.relative),
        lastActionAt: ms(la?.timestamp),
        online: s(la?.status) || 'Offline',
      };
    }),
  };
}
